import React, { useState, useEffect, useRef } from 'react';
import styles from './PublicTable.module.scss';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, Tooltip } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function PublicTable(props: PublicTableProps) {
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    // View mode - controlled by parent component via props, defaults to 'list'
    const viewMode: 'list' | 'card' | 'map' = props.viewMode || 'list';
    // Map location field - field to use for geocoding (e.g., 'POSTAL_CODE')
    const mapLocationField: string = props.mapLocationField || 'POSTAL_CODE';

    // Map view state
    const mapRef = useRef<L.Map>(null);
    const [geocodingCache, setGeocodingCache] = useState<{ [key: string]: [number, number] }>({});
    const [geocodingInProgress, setGeocodingInProgress] = useState<Set<string>>(new Set());
    const [popupStates, setPopupStates] = useState<{ [key: string]: { currentPage: number; totalPages: number } }>({});
    const [dataByLocation, setDataByLocation] = useState<{ [key: string]: any[] }>({});
    const [isGeocodingComplete, setIsGeocodingComplete] = useState(false);

    const handleSort = (field: string) => {
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
    };

    // Determine which column groups have data
    const groupsWithData = React.useMemo(() => {
        const groups = new Set<string>();
        
        props.data.forEach((dataLine: any) => {
            props.headers.forEach((header) => {
                if (header.column_group) {
                    const value = header.override ? header.override(dataLine) : dataLine[header.field];
                    // Check if value exists and is not "Ikke oppgitt" or similar empty states
                    if (value && value !== 'Ikke oppgitt' && value !== 'Ikke satt' && value !== 'Ingen titler' && value !== 'Ikke valgt') {
                        groups.add(header.column_group);
                    }
                }
            });
        });
        
        return groups;
    }, [props.headers, props.data]);

    // Filter headers to only include those whose groups have data (or have no group)
    const filteredHeaders = React.useMemo(() => {
        return props.headers.filter(header => {
            if (!header.column_group) return true;
            return groupsWithData.has(header.column_group);
        });
    }, [props.headers, groupsWithData]);

    // For collapsed groups, keep one placeholder header so the sub-header row and data cells
    // have a matching column under the collapsed group header cell (colSpan=1).
    const visibleHeaders = React.useMemo(() => {
        const collapsed = props.collapsedGroups;
        if (!collapsed || collapsed.size === 0) return filteredHeaders;

        const seenCollapsedGroups = new Set<string>();
        const result: any[] = [];

        for (const h of filteredHeaders) {
            if (h.column_group && collapsed.has(h.column_group)) {
                if (!seenCollapsedGroups.has(h.column_group)) {
                    seenCollapsedGroups.add(h.column_group);
                    // Insert a single placeholder column for the whole collapsed group
                    result.push({ ...h, _collapsedPlaceholder: true, label: '', field: '__collapsed__' + h.column_group });
                }
                // Skip the rest of the columns in this collapsed group
            } else {
                result.push(h);
            }
        }

        return result;
    }, [filteredHeaders, props.collapsedGroups]);

    const sortedData = React.useMemo(() => {
        // If parent provides onSort handlers, skip internal sorting
        const hasParentSorting = filteredHeaders.some(h => h.onSort);
        if (hasParentSorting) return props.data;
        
        if (!orderBy) return props.data;

        return [...props.data].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (aValue === bValue) return 0;
            if (order === 'asc') {
                return aValue < bValue ? -1 : 1;
            } else {
                return bValue < aValue ? -1 : 1;
            }
        });
    }, [props.data, orderBy, order, filteredHeaders]);

    const columnGroups = React.useMemo(() => {
        const collapsed = props.collapsedGroups || new Set<string>();
        const groups: Array<{ label: string; colSpan: number; isCollapsed?: boolean; icon?: string }> = [];
        let i = 0;

        while (i < filteredHeaders.length) {
            const header = filteredHeaders[i];
            if (!header.column_group) {
                groups.push({ label: '', colSpan: 1 });
                i++;
            } else {
                const groupName = header.column_group;
                const isCollapsed = collapsed.has(groupName);
                const icon = header.collapsed_icon;
                let count = 0;
                while (i < filteredHeaders.length && filteredHeaders[i].column_group === groupName) {
                    count++;
                    i++;
                }
                groups.push({ label: groupName, colSpan: isCollapsed ? 1 : count, isCollapsed, icon });
            }
        }

        return groups;
    }, [filteredHeaders, props.collapsedGroups]);

    const hasColumnGroups = filteredHeaders.some(header => header.column_group);

    // Fix for default markers in react-leaflet
    useEffect(() => {
        if (typeof window !== 'undefined') {
            delete (L.Icon.Default.prototype as any)._getIconUrl;
            L.Icon.Default.mergeOptions({
                iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
                iconUrl: require('leaflet/dist/images/marker-icon.png'),
                shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
            });
        }
    }, []);

    // Geocoding function with rate limiting and retry logic
    const geocodeLocation = async (locationValue: string, retryCount = 0): Promise<[number, number] | null> => {
        if (geocodingCache[locationValue]) {
            return geocodingCache[locationValue];
        }

        if (geocodingInProgress.has(locationValue)) {
            return null;
        }

        try {
            setGeocodingInProgress(prev => new Set(prev).add(locationValue));
            
            const url = `https://nominatim.openstreetmap.org/search?postalcode=${locationValue}&country=Norge&format=json&limit=1`;
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`Geocoding request failed with status: ${response.status}`);
            }
            
            const apiData = await response.json();
            
            if (apiData && apiData.length > 0) {
                const coordinates: [number, number] = [parseFloat(apiData[0].lat), parseFloat(apiData[0].lon)];
                
                setGeocodingCache(prev => ({
                    ...prev,
                    [locationValue]: coordinates
                }));
                
                return coordinates;
            }
            
            return null;
        } catch (error) {
            // Retry on HTTP/2 errors or network issues
            if (retryCount < 3) {
                const delay = (retryCount + 1) * 500; // Exponential backoff: 500ms, 1000ms, 1500ms
                await new Promise(resolve => setTimeout(resolve, delay));
                
                setGeocodingInProgress(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(locationValue);
                    return newSet;
                });
                
                return geocodeLocation(locationValue, retryCount + 1);
            }
            
            console.error(`Geocoding error for location ${locationValue}:`, error);
            return null;
        } finally {
            setGeocodingInProgress(prev => {
                const newSet = new Set(prev);
                newSet.delete(locationValue);
                return newSet;
            });
        }
    };

    // Process data for map view when viewMode is 'map'
    useEffect(() => {
        if (viewMode !== 'map') {
            return;
        }

        // Check if data is valid
        if (!sortedData || sortedData.length === 0) {
            return;
        }

        const processData = async () => {
            setIsGeocodingComplete(false);
            
            // Collect all unique location values and their corresponding data items
            const locationMap = new Map<string, any[]>();
            
            console.log('Starting geocoding for map view...');
            console.log(`Items to process: `, sortedData);
            for (const dataLine of sortedData) {
                const locationValue = (dataLine as any)[mapLocationField];
                const hasValidLocation = locationValue && locationValue !== 'Ikke oppgitt';
                
                if (hasValidLocation) {
                    if (!locationMap.has(locationValue)) {
                        locationMap.set(locationValue, []);
                    }
                    locationMap.get(locationValue)!.push(dataLine);
                }
            }
            
            // Fetch coordinates in batches to avoid overwhelming the server
            const locationValues = Array.from(locationMap.keys());
            const batchSize = 5; // Process 5 locations at a time
            const allCoordinates: (([number, number] | null))[] = [];
            
            for (let i = 0; i < locationValues.length; i += batchSize) {
                const batch = locationValues.slice(i, i + batchSize);
                const batchPromises = batch.map(location => geocodeLocation(location));
                const batchResults = await Promise.all(batchPromises);
                allCoordinates.push(...batchResults);
                
                // Update map with partial results after each batch
                const grouped: { [key: string]: any[] } = {};
                
                allCoordinates.forEach((coordinates, index) => {
                    if (coordinates) {
                        const locationValue = locationValues[index];
                        const items = locationMap.get(locationValue)!;
                        grouped[locationValue] = items;
                    }
                });
                
                setDataByLocation(grouped);
                
                // Add delay between batches to respect rate limits (Nominatim: 1 req/sec)
                if (i + batchSize < locationValues.length) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
            
            setIsGeocodingComplete(true);
        };

        processData();
    }, [viewMode, sortedData, mapLocationField]);

    const getFieldValue = (dataLine: any, header: any) => {
        const value = header.override ? header.override(dataLine) : dataLine[header.field];
        
        if (value === null || value === undefined) {
            return 'Ikke oppgitt';
        }
        
        // Handle dates
        if (header.field.includes('DATE') || header.field.includes('TIMESTAMP')) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('no-NO');
                }
            } catch {
                return value;
            }
        }
        
        return value;
    };

    const renderCardView = () => {
        if (!sortedData || sortedData.length === 0) {
            return (
                <div className={styles.noResults}>
                    <p>Ingen data tilgjengelig</p>
                </div>
            );
        }

        return (
            <div className={styles.cardsContainer}>
                {sortedData.map((dataLine: any, index: any) => {
                    const visibleHeaders = filteredHeaders.filter((header: any) => header != undefined);
                    
                    return (
                        <div 
                            key={index} 
                            className={styles.card}
                            onClick={() => props.onClickRow?.(dataLine)}
                            style={{ cursor: props.onClickRow ? 'pointer' : 'default' }}
                        >
                            <div className={styles.cardHeader}>
                                {visibleHeaders.slice(0, 2).map((header: any, fieldIndex: number) => (
                                    <div key={fieldIndex} className={styles.cardHeaderItem}>
                                        <span className={styles.cardHeaderLabel}>
                                            {header.label || header.field}
                                        </span>
                                        <span className={styles.cardHeaderValue}>
                                            {getFieldValue(dataLine, header)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            
                            <div className={styles.cardContent}>
                                {visibleHeaders.slice(2).map((header: any, fieldIndex: number) => {
                                    const value = getFieldValue(dataLine, header);
                                    if (!value || value === 'Ikke oppgitt') return null;
                                    
                                    return (
                                        <div key={fieldIndex} className={styles.cardField}>
                                            <span className={styles.cardFieldLabel}>
                                                {header.label || header.field}:
                                            </span>
                                            <span className={styles.cardFieldValue}>
                                                {value}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMapView = () => {
        const defaultCenter: [number, number] = [60.4720, 8.4689];
        const defaultZoom = 6;

        const getPopupState = (locationValue: string, totalItems: number) => {
            const state = popupStates[locationValue];
            const totalPages = Math.ceil(totalItems / 1);
            return state || { currentPage: 0, totalPages };
        };

        const updatePopupState = (locationValue: string, newState: { currentPage: number; totalPages: number }) => {
            setPopupStates(prev => ({
                ...prev,
                [locationValue]: newState
            }));
        };

        const navigatePopup = (locationValue: string, direction: 'prev' | 'next') => {
            const currentState = getPopupState(locationValue, dataByLocation[locationValue]?.length || 0);
            const newPage = direction === 'next' 
                ? Math.min(currentState.currentPage + 1, currentState.totalPages - 1)
                : Math.max(currentState.currentPage - 1, 0);
            
            updatePopupState(locationValue, { ...currentState, currentPage: newPage });
        };

        return (
            <div className={styles.mapContainer}>
                {!isGeocodingComplete && (
                    <div className={styles.loadingBanner}>
                        Laster inn lokasjoner... ({Object.keys(dataByLocation).length} funnet)
                    </div>
                )}
                <MapContainer
                    center={defaultCenter}
                    zoom={defaultZoom}
                    className={styles.map}
                    ref={mapRef}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {Object.entries(dataByLocation).map(([locationValue, locationData]) => {
                        const coordinates = geocodingCache[locationValue];
                        if (!coordinates || locationData.length === 0) return null;

                        const popupState = getPopupState(locationValue, locationData.length);
                        const currentItem = locationData[popupState.currentPage];
                        const hasMultipleItems = locationData.length > 1;
                        const visibleHeaders = filteredHeaders.filter((header: any) => header != undefined);

                        return (
                            <Marker key={locationValue} position={coordinates}>
                                <Popup maxWidth={320} maxHeight={500} className={styles.customPopup}>
                                    <div>
                                        {hasMultipleItems && (
                                            <div className={styles.paginationControls}>
                                                <div className={styles.paginationInfo}>
                                                    {popupState.currentPage + 1} av {locationData.length}
                                                </div>
                                                <div className={styles.paginationButtons}>
                                                    <button 
                                                        onClick={() => navigatePopup(locationValue, 'prev')}
                                                        disabled={popupState.currentPage === 0}
                                                        className={styles.paginationButton}
                                                    >
                                                        ← Forrige
                                                    </button>
                                                    <button 
                                                        onClick={() => navigatePopup(locationValue, 'next')}
                                                        disabled={popupState.currentPage === popupState.totalPages - 1}
                                                        className={styles.paginationButton}
                                                    >
                                                        Neste →
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <div className={styles.mapPopupCard}>
                                            <div className={styles.cardHeader}>
                                                {visibleHeaders.slice(0, 2).map((header: any, fieldIndex: number) => (
                                                    <div key={fieldIndex} className={styles.cardHeaderItem}>
                                                        <span className={styles.cardHeaderLabel}>
                                                            {header.label || header.field}
                                                        </span>
                                                        <span className={styles.cardHeaderValue}>
                                                            {getFieldValue(currentItem, header)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className={styles.cardContent}>
                                                {visibleHeaders.slice(2).map((header: any, fieldIndex: number) => {
                                                    const value = getFieldValue(currentItem, header);
                                                    if (!value || value === 'Ikke oppgitt') return null;
                                                    
                                                    return (
                                                        <div key={fieldIndex} className={styles.cardField}>
                                                            <span className={styles.cardFieldLabel}>
                                                                {header.label || header.field}:
                                                            </span>
                                                            <span className={styles.cardFieldValue}>
                                                                {value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {props.onClickRow && (
                                                <div className={styles.cardActions}>
                                                    <button
                                                        className={styles.selectButton}
                                                        onClick={() => props.onClickRow?.(currentItem)}
                                                    >
                                                        Velg
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>
        );
    };

    return (
        <div className={styles.publictable}>
            {viewMode === 'list' ? (
                <TableContainer component={Paper}>
                    <Table stickyHeader size="small" style={{ minWidth: 'max-content' }}>
                        <TableHead>
                            {hasColumnGroups && (
                                <TableRow
                                    className={styles.tableheadergrouping}
                                >
                                    {columnGroups.map((group, index) => (
                                        <TableCell
                                            key={index}
                                            className={group.label ? styles.tableheader : styles.tableheadertransparent}
                                            colSpan={group.colSpan}
                                            align="center"
                                            onClick={group.label && props.onGroupToggle ? () => props.onGroupToggle!(group.label) : undefined}
                                            style={group.label && props.onGroupToggle ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                                        >
                                            {group.isCollapsed && group.icon
                                                ? <span title={group.label}>{group.icon} ▶</span>
                                                : <>{group.label}{group.label && props.onGroupToggle ? (group.isCollapsed ? ' ▶' : ' ▼') : ''}</>
                                            }
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                            <TableRow>
                                {visibleHeaders.map((header, index) => (
                                    header._collapsedPlaceholder
                                        ? <TableCell key={index} className={`${styles.tableheader} ${styles.collapsedcell}`} />
                                        : <TableCell
                                        key={index}
                                        className={styles.tableheader}
                                        sortDirection={header.sortActive ? (header.sortDirection || order) : (orderBy === header.field ? order : false)}
                                        style={header.maxWidth ? { maxWidth: header.maxWidth, overflow: 'hidden', textOverflow: 'ellipsis' } : header.wrap ? { whiteSpace: 'normal' } : undefined}
                                    >
                                        <Tooltip title={header.tooltip || header.label} arrow>
                                            <TableSortLabel
                                                active={header.sortActive !== undefined ? header.sortActive : orderBy === header.field}
                                                direction={header.sortDirection || (orderBy === header.field ? order : 'asc')}
                                                onClick={() => header.onSort ? header.onSort() : handleSort(header.field)}
                                            >
                                                {header.label}
                                            </TableSortLabel>
                                        </Tooltip>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData?.map((data, index) => (
                                <TableRow
                                    key={index}
                                    className={styles.tablerow + " " + (props.onClickRow ? styles.tablerowclickable : '')}  
                                    onClick={() => props.onClickRow ? props.onClickRow(data) : null}
                                >
                                    {visibleHeaders.map((header, index) => (
                                        header._collapsedPlaceholder
                                            ? <TableCell key={index} className={`${styles.tablecell} ${styles.collapsedcell}`} />
                                            : <TableCell
                                            key={index}
                                            className={styles.tablecell}
                                            style={header.maxWidth ? { maxWidth: header.maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : header.wrap ? { whiteSpace: 'normal' } : { whiteSpace: 'nowrap' }}
                                        >
                                            {header.maxWidth ? (
                                                <Tooltip title={data[header.field] != null ? String(data[header.field]) : ''} arrow>
                                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {header.override ? header.override(data) : data[header.field]}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                header.override ? header.override(data) : data[header.field]
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : viewMode === 'card' ? (
                <div className={styles.cardViewContainer}>
                    {renderCardView()}
                </div>
            ) : (
                <div className={styles.mapViewContainer}>
                    {renderMapView()}
                </div>
            )}
        </div>
    );
}

class PublicTableProps {
    headers: Array<any> = []; // Each header can have: label, field, override, tooltip, column_group, maxWidth
    data: Array<any> = [];
    onClickRow?: (rowData: any) => void;
    viewMode?: 'list' | 'card' | 'map';
    mapLocationField?: string;
    collapsedGroups?: Set<string>;
    onGroupToggle?: (group: string) => void;
}

export default PublicTable;
